import { getSupabaseOrThrow } from '../supabaseClient'

const DEFAULT_BUCKET = 'journal-photos'
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
const MAX_INPUT_BYTES = 30 * 1024 * 1024

function getBucketName() {
  const fromEnv = (import.meta.env.VITE_SUPABASE_JOURNAL_PHOTOS_BUCKET as string | undefined) ?? ''
  return fromEnv.trim() || DEFAULT_BUCKET
}

function isBucketNotFoundError(message: string) {
  return /bucket not found/i.test(message)
}

function getCandidateBuckets() {
  const configured = getBucketName()
  return configured === DEFAULT_BUCKET ? [configured] : [configured, DEFAULT_BUCKET]
}

function getFileExtension(file: File) {
  const name = file.name || ''
  const lastDot = name.lastIndexOf('.')
  if (lastDot === -1) return 'jpg'
  const ext = name.slice(lastDot + 1).toLowerCase().trim()
  return ext || 'jpg'
}

function assertImageFile(file: File) {
  if (!file.type?.startsWith('image/')) {
    throw new Error('Wybierz plik graficzny (JPG/PNG/WebP).')
  }
  // Guardrail: very large files can easily OOM the browser during resize/compress.
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Plik jest za duży. Maksymalny rozmiar wejściowy to 30 MB.')
  }
}

async function fileToImageBitmap(file: File) {
  // createImageBitmap is fast and avoids DOM <img />; supported by modern browsers.
  return await createImageBitmap(file)
}

function pickTargetSize(srcW: number, srcH: number, opts: { maxSide: number; maxPixels: number }) {
  const maxSideScale = Math.min(1, opts.maxSide / Math.max(srcW, srcH))
  const maxPixelsScale = Math.min(1, Math.sqrt(opts.maxPixels / Math.max(1, srcW * srcH)))
  const scale = Math.min(maxSideScale, maxPixelsScale)
  const w = Math.max(1, Math.round(srcW * scale))
  const h = Math.max(1, Math.round(srcH * scale))
  return { w, h, scale }
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Nie udało się przygotować pliku do wysyłki.'))),
      type,
      quality,
    )
  })
}

async function maybeCompressForUpload(file: File) {
  // If already within limit, keep original (preserves best quality).
  if (file.size <= MAX_UPLOAD_BYTES) return file

  // For large JPEG/WebP, downscale + recompress to stay under the 10MB upload limit.
  // Goal: keep "album-worthy" resolution while avoiding upload errors/timeouts.
  const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg'
  const isWebp = file.type === 'image/webp'
  if (!isJpeg && !isWebp) {
    // PNG can easily exceed limits and re-encoding may destroy transparency.
    throw new Error('To zdjęcie jest zbyt duże. Użyj JPG/WebP lub zmniejsz plik przed wysyłką.')
  }

  const bitmap = await fileToImageBitmap(file)
  const { w, h } = pickTargetSize(bitmap.width, bitmap.height, {
    maxSide: 3200, // good print-ish / album size for most phones
    maxPixels: 12_000_000, // ~12MP
  })

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Nie udało się przygotować zdjęcia do wysyłki.')
  ctx.drawImage(bitmap, 0, 0, w, h)

  // Try a couple of qualities; keep the highest that fits the limit.
  const qualities = [0.9, 0.86, 0.82, 0.78]
  let bestBlob: Blob | null = null
  for (const q of qualities) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', q)
    bestBlob = blob
    if (blob.size <= MAX_UPLOAD_BYTES) break
  }
  if (!bestBlob) throw new Error('Nie udało się przygotować zdjęcia do wysyłki.')
  if (bestBlob.size > MAX_UPLOAD_BYTES) {
    throw new Error('Zdjęcie po kompresji nadal jest za duże. Spróbuj mniejszy plik.')
  }

  const baseName = (file.name || 'photo').replace(/\.[^/.]+$/, '')
  return new File([bestBlob], `${baseName}.jpg`, { type: 'image/jpeg' })
}

/**
 * Internal: upload przygotowanego pliku do bucketów-kandydatów (z fallbackiem),
 * ścieżka tworzona jako `{pathPrefix}/{uuid}.{ext}`.
 *
 * Wydzielone z `uploadJournalImage`, żeby moduł społeczności mógł użyć tej samej
 * logiki kompresji + fallback bucketa, ale pod prefiksem `community/{userId}/{postId}`.
 */
export async function uploadImageToBucket(input: { pathPrefix: string; file: File }) {
  const supabase = getSupabaseOrThrow()
  assertImageFile(input.file)

  const preparedFile = await maybeCompressForUpload(input.file)
  const ext = getFileExtension(preparedFile)
  const id = crypto.randomUUID()
  const cleanPrefix = input.pathPrefix.replace(/^\/+|\/+$/g, '')
  const path = `${cleanPrefix}/${id}.${ext}`
  const candidateBuckets = getCandidateBuckets()
  let lastErrorMessage = ''

  for (const bucket of candidateBuckets) {
    const { error } = await supabase.storage.from(bucket).upload(path, preparedFile, {
      upsert: false,
      contentType: preparedFile.type || undefined,
      cacheControl: '3600',
    })

    if (!error) {
      const { data } = supabase.storage.from(bucket).getPublicUrl(path)
      const publicUrl = data.publicUrl?.trim()
      if (!publicUrl) throw new Error('Nie udało się pobrać URL zdjęcia po uploadzie.')
      return { publicUrl, path, bucket }
    }

    lastErrorMessage = error.message || 'Nie udało się wysłać zdjęcia.'
    const shouldTryNextBucket = isBucketNotFoundError(lastErrorMessage) && bucket !== candidateBuckets[candidateBuckets.length - 1]
    if (!shouldTryNextBucket) {
      break
    }
  }

  if (isBucketNotFoundError(lastErrorMessage)) {
    throw new Error(
      `Nie znaleziono bucketa na zdjęcia (${candidateBuckets.join(' lub ')}). Ustaw poprawne VITE_SUPABASE_JOURNAL_PHOTOS_BUCKET albo utwórz bucket "${DEFAULT_BUCKET}" w Supabase Storage.`,
    )
  }
  throw new Error(lastErrorMessage || 'Nie udało się wysłać zdjęcia.')
}

export async function uploadJournalImage(input: { userId: string; placeId: number; file: File }) {
  return uploadImageToBucket({
    pathPrefix: `${input.userId}/${input.placeId}`,
    file: input.file,
  })
}

